import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

// Every key here must match a `module:` gate actually used somewhere in
// components/Sidebar.jsx / utils/routeModules.js — otherwise a module can be
// toggled here but nothing in the app actually checks it (or, as was the
// case for expenses/progressReports/nonTeachingStaff, a module gates real
// Admin/Principal nav items but was never toggleable from this screen at
// all). Keep this list in sync with those two files.
const ALL_MODULES = [
  { key: 'attendance', label: 'Attendance' },
  { key: 'fees', label: 'Fees' },
  { key: 'exams', label: 'Exams' },
  { key: 'leaves', label: 'Leaves' },
  { key: 'transport', label: 'Transport' },
  { key: 'salary', label: 'Salary' },
  { key: 'classes', label: 'Classes' },
  { key: 'communications', label: 'Communications' },
  { key: 'events', label: 'Events' },
  { key: 'materials', label: 'Materials' },
  { key: 'timetable', label: 'Timetable' },
  { key: 'dailyModules', label: 'Daily Modules' },
  { key: 'documents', label: 'Documents' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'progressReports', label: 'Progress Reports' },
  { key: 'nonTeachingStaff', label: 'Non-Teaching Staff' },
];

const generateLicenseKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().toUpperCase();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  }).toUpperCase();
};

const LicenseManagement = () => {
  const { user } = useAuth();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [pendingModules, setPendingModules] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    licenseKey: '',
    schoolName: '',
    expirationDate: '',
    maxStudents: -1,
    maxTeachers: -1,
    notes: '',
  });
  const [renewData, setRenewData] = useState({
    expirationDate: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLicense();
  }, []);

  const fetchLicense = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/license/current`);
      setLicense(response.data);
      setPendingModules(response.data.modules || []);
    } catch (error) {
      console.error('Error fetching license:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = (moduleKey) => {
    setPendingModules(prev =>
      prev.includes(moduleKey)
        ? prev.filter(m => m !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const handleSaveModules = async () => {
    setError('');
    setSuccess('');
    setSavingModules(true);
    try {
      const response = await axios.patch(`${API_BASE}/license/modules`, { modules: pendingModules }, { headers });
      setLicense(prev => ({ ...prev, modules: response.data.modules }));
      setSuccess('Modules updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update modules');
    } finally {
      setSavingModules(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('max') ? (value === '' ? -1 : parseInt(value)) : value
    }));
  };

  const handleRenewChange = (e) => {
    const { name, value } = e.target;
    setRenewData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.licenseKey || !formData.schoolName || !formData.expirationDate) {
      setError('License key, school name, and expiration date are required');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/license/activate`, formData, { headers });
      setSuccess(response.data.message);
      setLicense(response.data.license);
      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate license');
    }
  };

  const handleRenewLicense = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!renewData.expirationDate) {
      setError('Expiration date is required');
      return;
    }

    try {
      setIsRenewing(true);
      const response = await axios.post(`${API_BASE}/license/renew`, renewData, { headers });
      setSuccess(response.data.message);
      setLicense(response.data.license);
      setIsRenewing(false);
      setRenewData({ expirationDate: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to renew license');
      setIsRenewing(false);
    }
  };

  const handleDeactivateLicense = async () => {
    if (!window.confirm('Are you sure you want to deactivate this license?')) return;

    try {
      const response = await axios.post(`${API_BASE}/license/deactivate`, {}, { headers });
      setSuccess(response.data.message);
      setLicense(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate license');
    }
  };

  const resetForm = () => {
    setFormData({
      licenseKey: '',
      schoolName: '',
      expirationDate: '',
      maxStudents: -1,
      maxTeachers: -1,
      notes: '',
    });
  };

  const openLicenseModal = () => {
    if (license) {
      setFormData({
        licenseKey: license.licenseKey,
        schoolName: license.schoolName,
        expirationDate: license.expirationDate ? new Date(license.expirationDate).toISOString().split('T')[0] : '',
        maxStudents: license.maxStudents,
        maxTeachers: license.maxTeachers,
        notes: license.notes || '',
      });
    } else {
      setFormData({
        licenseKey: generateLicenseKey(),
        schoolName: '',
        expirationDate: '',
        maxStudents: -1,
        maxTeachers: -1,
        notes: '',
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleRegenerateKey = () => {
    setFormData(prev => ({ ...prev, licenseKey: generateLicenseKey() }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">✓ Active</span>;
      case 'expiring-soon':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">⚠️ Expiring Soon</span>;
      case 'expired':
        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">✗ Expired</span>;
      case 'inactive':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">⊘ Inactive</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">Unknown</span>;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-red-700 shadow-sm h-16 flex items-center justify-between px-6">
          <h1 className="text-2xl font-bold text-white">🔐 License Management</h1>
          <ProfileDropdown />
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Success Alert */}
            {success && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                ✓ {success}
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                ✗ {error}
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin">
                  <div className="h-12 w-12 border-4 border-red-600 border-r-transparent rounded-full"></div>
                </div>
                <p className="mt-4 text-gray-600">Loading license information...</p>
              </div>
            ) : license ? (
              <div className="space-y-6">
                {/* Current License Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">📋 Current License</h2>
                    {getStatusBadge(license.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">License Key</label>
                      <p className="text-lg font-mono text-gray-900 bg-gray-50 p-3 rounded-lg break-all">{license.licenseKey}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                      <p className="text-lg text-gray-900 bg-gray-50 p-3 rounded-lg">{license.schoolName}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                      <p className="text-lg text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {new Date(license.expirationDate).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Days Remaining</label>
                      <p className={`text-lg font-bold p-3 rounded-lg ${
                        license.status === 'expired' 
                          ? 'text-red-700 bg-red-50' 
                          : license.daysRemaining <= 30
                          ? 'text-yellow-700 bg-yellow-50'
                          : 'text-green-700 bg-green-50'
                      }`}>
                        {license.isExpired ? 'Expired' : `${license.daysRemaining} days`}
                      </p>
                    </div>

                    {license.maxStudents !== -1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                        <p className="text-lg text-gray-900 bg-gray-50 p-3 rounded-lg">{license.maxStudents}</p>
                      </div>
                    )}

                    {license.maxTeachers !== -1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Teachers</label>
                        <p className="text-lg text-gray-900 bg-gray-50 p-3 rounded-lg">{license.maxTeachers}</p>
                      </div>
                    )}
                  </div>

                  {license.notes && (
                    <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                      <p className="text-gray-700">{license.notes}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={openLicenseModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                    >
                      ✏️ Update License
                    </button>
                    {!license.isExpired && (
                      <button
                        onClick={() => setIsRenewing(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                      >
                        🔄 Renew License
                      </button>
                    )}
                    <button
                      onClick={handleDeactivateLicense}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
                    >
                      🔴 Deactivate
                    </button>
                  </div>
                </div>

                {/* Modules Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">📦 Module Access Control</h3>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {pendingModules ? pendingModules.length : 0}/{ALL_MODULES.length} enabled
                      </span>
                      <button
                        onClick={handleSaveModules}
                        disabled={savingModules}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                      >
                        {savingModules ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {ALL_MODULES.map((mod) => {
                      const enabled = pendingModules ? pendingModules.includes(mod.key) : false;
                      return (
                        <button
                          key={mod.key}
                          onClick={() => handleModuleToggle(mod.key)}
                          className={`px-3 py-3 rounded-lg border-2 text-sm font-medium transition text-left flex items-center gap-2 ${
                            enabled
                              ? 'bg-green-50 border-green-400 text-green-800'
                              : 'bg-gray-50 border-gray-200 text-gray-400'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            enabled ? 'bg-green-500 border-green-500' : 'border-gray-300'
                          }`}>
                            {enabled && <span className="text-white text-xs">✓</span>}
                          </span>
                          {mod.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-3">Click a module to toggle it on/off, then click Save Changes.</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-5xl mb-4">🔓</div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">No License Found</h2>
                <p className="text-gray-600 mb-6">The application doesn't have an active license. Please activate one to continue.</p>
                <button
                  onClick={openLicenseModal}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                >
                  + Activate License
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activate/Update License Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-red-600 via-red-600 to-rose-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-xl">
                  {license ? '✏️' : '🔐'}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white leading-tight">
                    {license ? 'Update License' : 'Activate License'}
                  </h2>
                  <p className="text-red-100 text-xs">
                    {license ? 'Modify the active license for this school' : 'Set up a new license to unlock the app'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="text-white/80 hover:text-white text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition"
              >
                ×
              </button>
            </div>

            {error && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">
                ✗ {error}
              </div>
            )}

            <form onSubmit={handleActivateLicense} className="p-6 space-y-6">
              {/* License Key Section */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  License Key <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="licenseKey"
                    value={formData.licenseKey}
                    readOnly
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg bg-white font-mono text-sm text-gray-700 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleRegenerateKey}
                    title="Generate a new random key"
                    className="px-3 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition text-sm font-medium whitespace-nowrap"
                  >
                    🔄 Regenerate
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Auto-generated as a unique key (UUID). Click Regenerate for a new one.</p>
              </div>

              {/* Basic Info Section */}
              <div className="space-y-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">School & Validity</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    School Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="schoolName"
                    value={formData.schoolName}
                    onChange={handleChange}
                    placeholder="Enter school name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="expirationDate"
                    value={formData.expirationDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  />
                </div>
              </div>

              {/* Limits Section */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Usage Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                    <input
                      type="number"
                      name="maxStudents"
                      value={formData.maxStudents}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    />
                    <p className="text-xs text-gray-400 mt-1">-1 = Unlimited</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Teachers</label>
                    <input
                      type="number"
                      name="maxTeachers"
                      value={formData.maxTeachers}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    />
                    <p className="text-xs text-gray-400 mt-1">-1 = Unlimited</p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any notes about this license..."
                  rows="3"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium shadow-sm"
                >
                  {license ? 'Update License' : 'Activate License'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Renew License Modal */}
      {isRenewing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
              <h2 className="text-lg font-bold text-white">🔄 Renew License</h2>
            </div>

            <form onSubmit={handleRenewLicense} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Expiration Date *
                </label>
                <input
                  type="date"
                  name="expirationDate"
                  value={renewData.expirationDate}
                  onChange={handleRenewChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                />
              </div>

              <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                Current expiration: {license && new Date(license.expirationDate).toLocaleDateString('en-IN')}
              </p>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsRenewing(false);
                    setRenewData({ expirationDate: '' });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isRenewing}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {isRenewing ? 'Renewing...' : 'Renew License'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseManagement;
